import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useParams, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useLanguage } from '@/hooks/useLanguage';

import { ModalProvider } from '@/utils/ModalContext/ModalProvider';
import { ScreenTypeProvider } from '@/utils/ScreenTypeContext/ScreenTypeProvider';
import { LanguageProvider } from '@/utils/LanguageContext/LanguageProvider';
import { NotificationProvider } from '@/utils/NotificationContext/NotificationProvider';

import { Header } from '@/components/Header';
import { Home } from '@/pages/Home';
import { Footer } from '@/components/Footer';
import { ModalWindow } from '@/components/ModalWindow';
import { Notification } from '@/components/Notification';

import styles from './App.module.scss';

function LanguageWrapper({ children }: { children: React.ReactNode }) {
  const { language } = useParams();
  const { setLanguage, languageOptions } = useLanguage();

  useEffect(() => {
    const validLanguage = languageOptions.find((option) => option.value === language);
    if (validLanguage) {
      setLanguage(validLanguage);
    } else {
      setLanguage(languageOptions[0]);
    }
  }, [language, setLanguage, languageOptions]);

  return children;
}

function MainElement() {
  return (
    <LanguageWrapper>
      <main className={styles.app}>
        <Header />
        <Home />
        <Footer />
        <ModalWindow />
        <Notification />
      </main>
    </LanguageWrapper>
  );
}

function Router() {
  const { selectedLanguage } = useLanguage();
  const { t } = useTranslation('meta');

  const baseUrl = 'https://annatransfer.com.ua';
  const langPath = selectedLanguage.value === 'uk' ? '' : `/${selectedLanguage.value}`;
  const canonicalUrl = `${baseUrl}${langPath}/`;

  return (
    <>
      <link rel="canonical" href={canonicalUrl} />
      <meta name="description" content={t('meta.description')} />
      <meta name="keywords" content={t('meta.keywords')} />
      <meta property="og:title" content="Anna Transfer" />
      <meta property="og:description" content={t('meta.og.description')} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://annatransfer.com.ua/" />
      <meta property="og:image" content="/logo.svg" />
      <link rel="alternate" href="https://site.com/" hrefLang="uk" />
      <link rel="alternate" href="https://site.com/en" hrefLang="en" />
      <link rel="alternate" href="https://site.com/fr" hrefLang="fr" />
      <title>Anna Transfer</title>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<MainElement />} />
          <Route path="/:language" element={<MainElement />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </BrowserRouter>
    </>
  );
}

export function App() {
  return (
    <LanguageProvider>
      <ScreenTypeProvider>
        <NotificationProvider>
          <ModalProvider>
            <Router />
          </ModalProvider>
        </NotificationProvider>
      </ScreenTypeProvider>
    </LanguageProvider>
  );
}
